import {
  DecodeError,
  NetworkError,
  loadHlsMediaPlaylist
} from "./chunk-2UKM3YYQ.js";
import {
  dimensionsToOrientation,
  fetchText
} from "./chunk-CXYZ37JF.js";
import {
  isHlsMediaType
} from "./chunk-PDD4MAGE.js";
import {
  toError
} from "./chunk-2GHSQBQ5.js";

// src/utils/hls-master-playlist.ts
import { Parser } from "m3u8-parser";

// src/utils/codecs.ts
var sampleEntryCodesISO = {
  audio: {
    a3ds: true,
    "ac-3": true,
    "ac-4": true,
    alac: true,
    alaw: true,
    dra1: true,
    "dts+": true,
    "dts-": true,
    dtsc: true,
    dtse: true,
    dtsh: true,
    "ec-3": true,
    enca: true,
    g719: true,
    g726: true,
    m4ae: true,
    mha1: true,
    mha2: true,
    mhm1: true,
    mhm2: true,
    mlpa: true,
    mp4a: true,
    "raw ": true,
    Opus: true,
    samr: true,
    sawb: true,
    sawp: true,
    sevc: true,
    sqcp: true,
    ssmv: true,
    twos: true,
    ulaw: true
  },
  video: {
    avc1: true,
    avc2: true,
    avc3: true,
    avc4: true,
    avcp: true,
    av01: true,
    drac: true,
    dvav: true,
    dvhe: true,
    encv: true,
    hev1: true,
    hvc1: true,
    mjp2: true,
    mp4v: true,
    mvc1: true,
    mvc2: true,
    mvc3: true,
    mvc4: true,
    resv: true,
    rv60: true,
    s263: true,
    svc1: true,
    svc2: true,
    "vc-1": true,
    vp08: true,
    vp09: true
  }
};
function isCodecType(codec, type) {
  const typeCodes = sampleEntryCodesISO[type];
  return !!typeCodes && typeCodes[codec.slice(0, 4)] === true;
}

// src/utils/hls-master-playlist.ts
var HLS_CAPTIONS_CHARACTERISTICS = "public.accessibility.describes-music-and-sound";
async function loadHlsMasterPlaylist(url, options) {
  let data;
  try {
    data = await fetchText(url, options);
  } catch (e) {
    const error = toError(e);
    if (error.name === "AbortError") {
      throw error;
    }
    throw new NetworkError(`Failed to fetch master playlist: ${url}`, error);
  }
  return parseHlsMasterPlaylist(url, data);
}
function parseHlsMasterPlaylist(baseUrl, data) {
  const parser = new Parser();
  try {
    parser.push(data);
    parser.end();
  } catch (e) {
    throw new DecodeError("Failed to parse media playlist.", toError(e));
  }
  const manifest = parser.manifest;
  const subtitleGroupIds = /* @__PURE__ */ new Set();
  let isAudioOnly = true;
  let orientation;
  const levels = manifest?.playlists?.map((level) => {
    const subtitleGroupId = level.attributes.SUBTITLES;
    if (subtitleGroupId !== void 0) {
      subtitleGroupIds.add(subtitleGroupId);
    }
    const codecs = level.attributes.CODECS?.split(",") ?? [];
    if (isAudioOnly) {
      for (let i = 0; i < codecs.length; ++i) {
        if (!isCodecType(codecs[i], "audio")) {
          isAudioOnly = false;
          break;
        }
      }
    }
    const quality = attributesToQuality(level.attributes);
    if (orientation === void 0 && quality.width !== void 0) {
      orientation = dimensionsToOrientation(quality.width, quality.height);
    }
    return {
      quality,
      url: new URL(level.uri, baseUrl).href
    };
  }) ?? [];
  const subtitles = getSubtitles(manifest, baseUrl, Array.from(subtitleGroupIds));
  return {
    isAudioOnly,
    levels,
    orientation,
    subtitles
  };
}
function levelsToQualities(levels) {
  const qualities = removeDuplicateQualities(levels.map((level) => level.quality));
  qualities.sort((a, b) => a.bitrate - b.bitrate);
  qualities.forEach((q, index) => q.level = index);
  return qualities;
}
function findNearestLevel(levels, bitrate) {
  let nearestDistance = Number.POSITIVE_INFINITY;
  let nearestLevelIndex = 0;
  levels.forEach((level, index) => {
    const distance = Math.abs(level.quality.bitrate - bitrate);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestLevelIndex = index;
    }
  });
  return levels[nearestLevelIndex];
}
function attributesToQuality(attributes) {
  const averageBitrate = attributes["AVERAGE-BANDWIDTH"] ? Number.parseInt(attributes["AVERAGE-BANDWIDTH"], 10) : void 0;
  const quality = {
    level: -1,
    bitrate: attributes.BANDWIDTH ?? averageBitrate ?? 0,
    averageBitrate,
    frameRate: attributes["FRAME-RATE"]
  };
  const resolution = attributes.RESOLUTION;
  if (resolution !== void 0) {
    quality.width = resolution.width;
    quality.height = resolution.height;
  }
  return quality;
}
function removeDuplicateQualities(qualities) {
  const seen = {};
  return qualities.filter((q) => {
    const asString = JSON.stringify(q);
    if (seen[asString]) {
      return false;
    }
    seen[asString] = true;
    return true;
  });
}
function getSubtitles(manifest, baseUrl, groupIds) {
  const subtitles = [];
  if (groupIds.length === 0) {
    return subtitles;
  }
  const groupId = groupIds[0];
  const subtitlesGroup = manifest.mediaGroups.SUBTITLES[groupId];
  for (const label of Object.keys(subtitlesGroup)) {
    const track = subtitlesGroup[label];
    const {
      language,
      autoselect: isAutoSelectable,
      forced: isForced,
      characteristics,
      default: isDefault,
      uri
    } = track;
    const isCaptions = characteristics?.split(",").includes(HLS_CAPTIONS_CHARACTERISTICS);
    subtitles.push({
      label,
      language,
      flags: {
        isAutoSelectable: isAutoSelectable || isCaptions || isDefault || isForced || false,
        isCaptions: isCaptions ?? false,
        isDefault: isDefault ?? false,
        isForced: isForced ?? false
      },
      groupId,
      description: characteristics,
      src: new URL(uri, baseUrl).href
    });
  }
  return subtitles;
}

// src/adapter/hls-media-info.ts
var HlsMediaInfo = class {
  id;
  url;
  type = "hls";
  mediaType;
  qualities;
  firstQuality;
  masterPlaylist;
  mediaPlaylist;
  constructor(mediaInfo) {
    if (mediaInfo.mediaType === void 0 || !isHlsMediaType(mediaInfo.mediaType)) {
      throw new Error("Bad use of HlsMediaInfo. Not a HLS media type.");
    }
    this.id = mediaInfo.id;
    this.url = mediaInfo.url;
    this.mediaType = mediaInfo.mediaType;
  }
  // Media info:
  get isAudioOnly() {
    return this.masterPlaylist?.isAudioOnly ?? false;
  }
  get videoOrientation() {
    return this.masterPlaylist?.orientation;
  }
  // HLS media playlist details:
  get playlistDuration() {
    return this.mediaPlaylist?.playlistDuration ?? 0;
  }
  get averageSegmentDuration() {
    return this.mediaPlaylist?.averageSegmentDuration ?? 0;
  }
  get programDateTime() {
    const pdt = this.mediaPlaylist?.programDateTime;
    return pdt ? pdt.getTime() / 1e3 : void 0;
  }
  /**
   * Loads and stores the master playlist. Required to complete the media info.
   */
  async loadMasterPlaylist(options) {
    this.masterPlaylist = await loadHlsMasterPlaylist(this.url, options);
    this.firstQuality = this.findLevel().quality;
    this.qualities = levelsToQualities(this.masterPlaylist.levels);
  }
  /**
   * Loads and stores a media playlist, referenced in the master playlist.
   */
  async loadMediaPlaylist(options) {
    const level = this.findLevel(options?.bitrate);
    this.mediaPlaylist = await loadHlsMediaPlaylist(level.url, options);
  }
  /**
   * Returns the first level or the level closest to the given bitrate.
   */
  findLevel(bitrate) {
    if (this.masterPlaylist === void 0) {
      throw new Error("Have to load master playlist first.");
    }
    if (this.masterPlaylist.levels.length === 0) {
      throw new Error("Master playlist has no quality levels.");
    }
    if (bitrate === void 0) {
      return this.masterPlaylist.levels[0];
    }
    return findNearestLevel(this.masterPlaylist.levels, bitrate);
  }
};

export {
  HlsMediaInfo
};
